"use client";

import { useEffect, useRef, useState } from "react";
import { InlineInfoBox } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";

interface WithdrawalConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (withdrawalReason: string) => void;
  policyNumber?: string;
  cardLastFourDigits?: string;
  modalTitle?: string;
  modalIntroText?: string;
  modalWarningText?: string;
  modalRefundInfoText?: string;
  modalOptionalText?: string;
  modalReasonPlaceholder?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export default function WithdrawalConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  policyNumber,
  cardLastFourDigits,
  modalTitle,
  modalIntroText,
  modalWarningText,
  modalRefundInfoText,
  modalOptionalText,
  modalReasonPlaceholder,
  confirmButtonText,
  cancelButtonText,
}: Readonly<WithdrawalConfirmationModalProps>) {
  const modalRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const STORAGE_KEY = "withdrawal_reason";
  const [withdrawalReason, setWithdrawalReason] = useState("");

  useEffect(() => {
    const savedReason = sessionStorage.getItem(STORAGE_KEY);
    if (savedReason) {
      setWithdrawalReason(savedReason);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    modalRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && textareaRef.current && withdrawalReason) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
      }, 0);
    }
  }, [isOpen, withdrawalReason]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setWithdrawalReason(value);
    sessionStorage.setItem(STORAGE_KEY, value);

    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const handleConfirmClick = () => {
    onConfirm(withdrawalReason);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  if (!isOpen) return null;

  const formatIntroText = (text: string) => {
    if (!policyNumber) return text;
    return text.replace("#POLICY_NUMBER", `<strong>${policyNumber}</strong>`);
  };

  const formatRefundInfoText = (text: string) => {
    if (!cardLastFourDigits) return text;
    return text.replace("#CARD_LAST_FOUR", cardLastFourDigits);
  };

  const handleBackdropClick = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.currentTarget === event.target) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      handleBackdropClick(event);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[103] flex items-center justify-center p-spacing-m bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      <div
        ref={modalRef}
        className="relative bg-surface-neutral-option-1 rounded-2xl w-full md:max-w-[442px] lg:max-w-[586px] h-full md:h-auto md:max-h-[90vh] overflow-y-auto p-spacing-l md:p-spacing-xl shadow-lg focus:outline-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <h2
          id="modal-title"
          className="font-header-font text-3xl text-text-title font-normal leading-[34px] mb-spacing-l text-center"
        >
          {modalTitle}
        </h2>

        {modalIntroText && (
          <div
            className="font-body-font text-xs font-normal text-text-body leading-[27px] text-center pb-[9px]"
            dangerouslySetInnerHTML={{
              __html: formatIntroText(modalIntroText),
            }}
          />
        )}

        {modalWarningText && (
          <p className="font-body-font text-xs font-normal text-text-body leading-[27px] mb-spacing-l text-center">
            {modalWarningText}
          </p>
        )}

        {modalRefundInfoText && (
          <div className="mb-spacing-l">
            <InlineInfoBox
              tabIndex={-1}
              className="!pl-spacing-m !pr-spacing-m !py-spacing-xs w-full font-body-font font-normal text-2xs text-text-functional leading-[24px] !rounded-lg !border-1 !border-border-positive-primary !bg-surface-positive"
              content={
                <RichText
                  richText={formatRefundInfoText(modalRefundInfoText)}
                />
              }
            />
          </div>
        )}

        {modalOptionalText && (
          <div className="mb-spacing-l">
            <p className="font-body-font text-2xs text-text-functional leading-6 mb-spacing-xxs">
              {modalOptionalText}
            </p>
            <textarea
              ref={textareaRef}
              id="withdrawal-reason"
              // rows={1}
              maxLength={250}
              value={withdrawalReason}
              onChange={handleReasonChange}
              className="w-full h-[80px] md:h-[56px] px-spacing-m py-spacing-s border border-interactive-secondary-default rounded-lg font-body-font text-2xs text-text-functional-subtle placeholder:text-text-functional-subtle resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-interactive-focus-default focus:border-transparent"
              placeholder={modalReasonPlaceholder}
            />
          </div>
        )}

        <div className="space-y-spacing-xs">
          <LabelButtonWrapper
            id="withdrawal-modal-confirm-button"
            data-testid="modal-confirm-button"
            isDisabled={false}
            label={confirmButtonText || ""}
            hierarchy="primary"
            className="w-full !h-size-xl rounded-[120px] font-body-font text-xs font-semibold text-text-on-primary !leading-[18px]"
            type="button"
            onClick={handleConfirmClick}
          />

          <LabelButtonWrapper
            id="withdrawal-modal-cancel-button"
            data-testid="modal-cancel-button"
            isDisabled={false}
            label={cancelButtonText || ""}
            hierarchy="secondary"
            className="w-full h-size-xl rounded-[120px] font-body-font text-xs font-semibold border-[1.5px] text-text-on-secondary !leading-[18px]"
            type="button"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
}
